import { ChevronDown, ChevronRight } from 'lucide-react';
import { channelLabel, type ChannelRow } from '../../lib/channels';
import { subscriberFunnelStatusLabel } from '../../lib/subscriberFunnelStatus';
import { subscriberSourceLabel } from '../../lib/subscriberSource';

type SubscriberListFiltersPanelProps = {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  channelSlugs: string[];
  channelCatalog: ChannelRow[];
  selectedChannelFilters: string[];
  onToggleChannel: (slug: string) => void;
  onClearFilters: () => void;
  emailStatusFilter: string;
  onEmailStatusFilterChange: (status: string) => void;
  availableEmailStatuses: string[];
  funnelStatusFilter: string;
  onFunnelStatusFilterChange: (status: string) => void;
  availableFunnelStatuses: string[];
  sourceFilter: string;
  onSourceFilterChange: (source: string) => void;
  availableSources: string[];
  needsInviteFilter: boolean;
  onNeedsInviteFilterChange: (active: boolean) => void;
  accountFilter: '' | 'linked' | 'unlinked';
  onAccountFilterChange: (value: '' | 'linked' | 'unlinked') => void;
};

function statusLabel(status: string): string {
  return status
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function SubscriberListFiltersPanel({
  collapsed,
  onCollapsedChange,
  channelSlugs,
  channelCatalog,
  selectedChannelFilters,
  onToggleChannel,
  onClearFilters,
  emailStatusFilter,
  onEmailStatusFilterChange,
  availableEmailStatuses,
  funnelStatusFilter,
  onFunnelStatusFilterChange,
  availableFunnelStatuses,
  sourceFilter,
  onSourceFilterChange,
  availableSources,
  needsInviteFilter,
  onNeedsInviteFilterChange,
  accountFilter,
  onAccountFilterChange,
}: SubscriberListFiltersPanelProps) {
  const hasActiveFilters =
    selectedChannelFilters.length > 0 ||
    emailStatusFilter.length > 0 ||
    funnelStatusFilter.length > 0 ||
    sourceFilter.length > 0 ||
    accountFilter.length > 0 ||
    needsInviteFilter;

  const summaryParts: string[] = [];
  if (selectedChannelFilters.length > 0) {
    summaryParts.push(
      `${selectedChannelFilters.length} channel${selectedChannelFilters.length === 1 ? '' : 's'}`,
    );
  }
  if (emailStatusFilter) {
    summaryParts.push(statusLabel(emailStatusFilter));
  }
  if (funnelStatusFilter) {
    summaryParts.push(subscriberFunnelStatusLabel(funnelStatusFilter) || statusLabel(funnelStatusFilter));
  }
  if (sourceFilter) {
    summaryParts.push(subscriberSourceLabel(sourceFilter) || statusLabel(sourceFilter));
  }
  if (accountFilter) {
    summaryParts.push(accountFilter === 'linked' ? 'Has account' : 'No account');
  }
  if (needsInviteFilter) {
    summaryParts.push('Needs invite');
  }

  return (
    <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="w-4 h-4 shrink-0" aria-hidden />
          )}
          Filters
          {summaryParts.length > 0 ? (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              ({summaryParts.join(' · ')})
            </span>
          ) : null}
        </button>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {!collapsed ? (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-1 md:grid-cols-[40%_minmax(0,1fr)] gap-4 md:gap-6 items-start">
            <div className="min-w-0">
              <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">Channels</p>
              {channelSlugs.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {channelSlugs.map((slug) => {
                    const active = selectedChannelFilters.includes(slug);
                    const catalogEntry = channelCatalog.find((c) => c.slug === slug);
                    return (
                      <button
                        key={slug}
                        type="button"
                        onClick={() => onToggleChannel(slug)}
                        aria-pressed={active}
                        className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                          active
                            ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                        title={catalogEntry?.description ?? slug}
                      >
                        {channelLabel(slug, channelCatalog)}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No channels configured yet.</p>
              )}
              {selectedChannelFilters.length > 1 ? (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Showing connections in all selected channels.
                </p>
              ) : null}
            </div>

            <div className="min-w-0 flex flex-col gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <label
                    htmlFor="subscriber-email-status-filter"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    Email status
                  </label>
                  <select
                    id="subscriber-email-status-filter"
                    value={emailStatusFilter}
                    onChange={(e) => onEmailStatusFilterChange(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All email statuses</option>
                    {availableEmailStatuses.map((status) => (
                      <option key={status} value={status}>
                        {statusLabel(status)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <label
                    htmlFor="subscriber-funnel-status-filter"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    Funnel status
                  </label>
                  <select
                    id="subscriber-funnel-status-filter"
                    value={funnelStatusFilter}
                    onChange={(e) => onFunnelStatusFilterChange(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All funnel statuses</option>
                    {availableFunnelStatuses.map((status) => (
                      <option key={status} value={status}>
                        {subscriberFunnelStatusLabel(status) || statusLabel(status)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <label
                    htmlFor="subscriber-source-filter"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    Source
                  </label>
                  <select
                    id="subscriber-source-filter"
                    value={sourceFilter}
                    onChange={(e) => onSourceFilterChange(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All sources</option>
                    {availableSources.map((source) => (
                      <option key={source} value={source}>
                        {subscriberSourceLabel(source) || source}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <label
                    htmlFor="subscriber-account-filter"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    Account
                  </label>
                  <select
                    id="subscriber-account-filter"
                    value={accountFilter}
                    onChange={(e) =>
                      onAccountFilterChange(e.target.value as '' | 'linked' | 'unlinked')
                    }
                    className="w-full px-2 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All accounts</option>
                    <option value="linked">Has linked account</option>
                    <option value="unlinked">No linked account</option>
                  </select>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={needsInviteFilter}
                  onChange={(e) => onNeedsInviteFilterChange(e.target.checked)}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                Needs newsletter invite (raw CRM contacts)
              </label>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
